import React from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';

const Grossesse = () => {
  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl md:text-5xl font-semibold text-[#6B5845] mb-4">
              Séance Photo Grossesse
            </h1>
            <p className="font-heading text-xl text-[#8B7355]">
              Sublimer la beauté de la maternité
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6 max-w-4xl mx-auto">
            <p className="font-body text-base text-[#6B5845] leading-relaxed">
              La grossesse est une période unique, intense, remplie d'émotions. Votre corps change, votre ventre s'arrondit, et déjà l'amour grandit. Ce moment si précieux mérite d'être capturé avec douceur, élégance et sensibilité.
            </p>
            <p className="font-body text-base text-[#6B5845] leading-relaxed">
              La séance grossesse, quant à elle, se planifie généralement entre la 28e et 34e semaine de grossesse, lorsque votre ventre est joliment arrondi.
            </p>
          </div>
        </div>
      </section>

      {/* Photos Gallery Hero */}
      <section className="py-20 bg-[#F5EDE4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-lg shadow-lg overflow-hidden service-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_woolurban/artifacts/c3i5lne0_4N9A8394.jpg" 
                alt="Séance photo grossesse Haguenau - Photo noir et blanc" 
                className="w-full h-96 object-cover"
              />
            </div>
            <div className="rounded-lg shadow-lg overflow-hidden service-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_woolurban/artifacts/p8lfkszd_photo%2068.jpeg" 
                alt="Photographe grossesse Strasbourg - Robe noire dentelle" 
                className="w-full h-96 object-cover"
              />
            </div>
            <div className="rounded-lg shadow-lg overflow-hidden service-card">
              <img 
                src="https://customer-assets.emergentagent.com/job_woolurban/artifacts/whmsasxd_4N9A4994.jpg" 
                alt="Séance maternité Bas-Rhin - Top blanc jean" 
                className="w-full h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6 max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#6B5845]">
              Spécialisée en photographie de maternité depuis plus de neuf ans
            </h2>
            <p className="font-body text-base text-[#6B5845] leading-relaxed">
              Je propose des séances photo grossesse en studio dans une ambiance chaleureuse et rassurante, à proximité de Strasbourg et Haguenau, en Alsace.
            </p>
            <p className="font-body text-base text-[#6B5845] leading-relaxed">
              Je mets à votre disposition une sélection de tenues soigneusement choisies pour sublimer les jolis ventres ronds des futures mamans : robes fluides, voiles, bodys, tissus délicats… Chaque pièce est pensée pour mettre en valeur votre silhouette avec raffinement.
            </p>
            <p className="font-body text-base text-[#6B5845] leading-relaxed">
              Je mets un point d'honneur à ce que chaque femme se sente belle, confiante et écoutée pendant sa séance. Vous n'avez pas besoin de savoir poser : je vous guide à chaque instant, avec douceur et bienveillance, pour que ce moment reste un véritable souvenir agréable et naturel.
            </p>
            <p className="font-body text-base text-[#6B5845] leading-relaxed">
              Mon métier, c'est aussi de vous mettre à l'aise, de capter votre lumière, vos émotions, vos gestes instinctifs, pour créer des images vraies, intemporelles, que vous aimerez redécouvrir encore et encore.
            </p>
          </div>
        </div>
      </section>

      {/* Timing Section */}
      <section className="py-20 bg-[#F5EDE4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="rounded-lg shadow-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_woolurban/artifacts/ypxwgb1b_IMG_2639.JPG" 
                alt="Photo grossesse Haguenau - Robe noire allongée" 
                className="w-full h-[600px] object-cover object-center"
              />
            </div>
            <div className="space-y-6">
              <h3 className="font-heading text-2xl font-semibold text-[#6B5845]">
                Le moment idéal pour votre séance
              </h3>
              <p className="font-body text-base text-[#6B5845] leading-relaxed">
                La séance grossesse se réalise idéalement entre la 7e et la 8e mois de grossesse, lorsque votre ventre est joliment rond et que vous vous sentez encore bien dans votre corps.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#6B5845] mb-4">
                Combien coûte une séance photo Grossesse ?
              </h2>
            </div>

            <Card className="bg-white border-[#E8DCC8] max-w-3xl mx-auto">
              <CardContent className="p-8 md:p-12">
                <div className="text-center mb-8">
                  <p className="font-body text-base text-[#6B5845] mb-2">
                    Séance Grossesse à partir de
                  </p>
                  <div className="font-heading text-5xl font-bold text-[#8B7355]">
                    290€
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="font-body text-base text-[#6B5845] font-medium">
                    Ce tarif comprend :
                  </p>
                  <ul className="space-y-3">
                    <li className="font-body text-base text-[#6B5845] flex items-start">
                      <span className="text-[#8B7355] mr-2">•</span>
                      <span>Une séance photo en studio dans une ambiance douce et chaleureuse</span>
                    </li>
                    <li className="font-body text-base text-[#6B5845] flex items-start">
                      <span className="text-[#8B7355] mr-2">•</span>
                      <span>La mise à disposition de tenues sélectionnées pour sublimer votre ventre rond</span>
                    </li>
                    <li className="font-body text-base text-[#6B5845] flex items-start">
                      <span className="text-[#8B7355] mr-2">•</span>
                      <span>Mon accompagnement personnalisé et bienveillant tout au long du shooting</span>
                    </li>
                    <li className="font-body text-base text-[#6B5845] flex items-start">
                      <span className="text-[#8B7355] mr-2">•</span>
                      <span>Les retouches professionnelles de vos belles images</span>
                    </li>
                  </ul>
                  <p className="font-body text-base text-[#8B7355] pt-4">
                    Votre compagnon et vos ainés sont les bienvenus pour partager ce grand moment.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="text-center space-y-4 pt-8">
              <p className="font-body text-base text-[#6B5845] leading-relaxed max-w-3xl mx-auto">
                Réservez votre moment de douceur dès maintenant pour garder un souvenir inoubliable de votre maternité.
              </p>
              <p className="font-body text-base text-[#6B5845] leading-relaxed max-w-3xl mx-auto">
                Seule, en couple ou avec vos enfants, je vous accueille dans un studio cosy pensé pour vous offrir un moment de douceur, de confiance et de beauté.
              </p>
              <p className="font-body text-base text-[#8B7355] leading-relaxed max-w-3xl mx-auto">
                Offrez-vous ce cadeau inestimable : des souvenirs d'une féminité puissante et tendre, à transmettre à votre enfant, à garder pour la vie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bundle Offer Section */}
      <section className="py-20 bg-[#F5EDE4]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-[#F5EDE4] to-[#E8DCC8] border-2 border-[#8B7355] shadow-xl">
            <CardContent className="p-8 md:p-12 text-center space-y-6">
              <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#6B5845]">
                SUIVI MATERNITÉ : OFFRE POUR DES SOUVENIRS PRÉCIEUX & COMPLET!
              </h3>
              <div className="flex items-center justify-center space-x-4">
                <span className="font-heading text-3xl text-[#6B5845] line-through opacity-60">
                  659€
                </span>
                <span className="font-heading text-5xl font-bold text-[#8B7355]">
                  549€
                </span>
              </div>
              <p className="font-body text-base text-[#6B5845] leading-relaxed max-w-3xl mx-auto">
                Immortaliser votre histoire en choisissant le forfait suivi maternité : une séance grossesse + une séance naissance, pour capturer chaque étape de cette aventure unique à un tarif privilégié.
              </p>
              <Link to="/contact">
                <Button className="bg-[#8B7355] hover:bg-[#6B5845] text-white font-body px-10 py-6 text-base rounded-lg btn-primary mt-4">
                  Réserver le forfait complet
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <p className="font-body text-base text-[#6B5845] leading-relaxed max-w-3xl mx-auto">
              Basée en Alsace, je reçois des familles venues de Strasbourg, Haguenau, Gundershoffen, Brumath, Bischwiller, Obernai, de Mulhouse et bien au-delà. Contactez-moi pour discuter de votre projet, je serai ravie de vous accompagner.
            </p>
            <Link to="/contact">
              <Button className="bg-[#8B7355] hover:bg-[#6B5845] text-white font-body px-8 py-6 text-base rounded-lg btn-primary">
                Contactez-moi
              </Button>
            </Link>
            <p className="font-body text-base text-[#8B7355] leading-relaxed max-w-3xl mx-auto pt-6">
              Offrez-vous un moment unique pour célébrer la vie, l'amour, et les liens familiaux, dans un cadre professionnel, bienveillant et tout en douceur.
            </p>
          </div>
        </div>
      </section>

      {/* Instagram CTA Section */}
      <section className="py-20 bg-[#F5EDE4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-heading text-3xl font-semibold text-[#6B5845]">
            Suivez nos créations sur Instagram
          </h2>
          <p className="font-body text-base text-[#6B5845] leading-relaxed">
            Découvrez nos dernières séances photo maternité et naissance, nos coulisses et bien plus encore !
          </p>
          <a 
            href="https://www.instagram.com/yastudio_photography/" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button className="bg-gradient-to-r from-[#E1306C] to-[#FD1D1D] hover:opacity-90 text-white font-body px-8 py-6 text-base rounded-lg btn-primary inline-flex items-center space-x-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <span>Suivez-nous sur Instagram</span>
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Grossesse;